import React, { useRef, useState } from 'react';
import ShieldCheckIcon from '../ShieldCheckIcon';
import { useHistory } from 'react-router-dom';
import { FiHome, FiUser, FiArrowRight } from 'react-icons/fi';
import { initials } from '../Header';
import Dropdown from '../Dropdown';
import { toast } from '../Toast';
import { TIME_ZONE_OPTIONS } from '../timeZones';
import { SettingsHeader, SettingsActions, RecordBanner, useSettingsSub } from './ClientSettings';
import { PEOPLE, useClient } from './clientSettingsData';

// UI only for now (user decision 2026-10-13): values come from state.currentStock and nothing is saved. The real
// save (CompanySettingsController + Stock.TimeZone / FinancialYearEnd) is parked in tmp/figma-impl/company-settings-backend.patch.
const DEFAULT_TIME_ZONE = 'Australia/Sydney';
const YEAR_ENDS = ['30 June', '31 December', '31 March', '30 September'].map(x => ({ value: x, label: x }));
// PNG / JPG only: the logo goes into emails, and most email clients don't show SVG
const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg'];

interface Form {
    website: string;
    timeZone: string;
    yearEnd: string;
    // null = no logo; a blob: url while a new one is only previewed
    logo: string | null;
}

const Company: React.FC = () => {
    const history = useHistory();
    const { stock, name, ticker, exchange } = useClient();
    const sub = useSettingsSub();
    const initial: Form = { website: stock?.websiteUrl ?? '', timeZone: DEFAULT_TIME_ZONE, yearEnd: YEAR_ENDS[0].value, logo: stock?.companyLogoUrl ?? null };
    const [form, setForm] = useState<Form>(initial);
    const [saved, setSaved] = useState<Form>(initial);
    const fileRef = useRef<HTMLInputElement>(null);
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    const set = (field: keyof Form, value: string | null) => setForm(f => ({ ...f, [field]: value }));

    const pickLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!LOGO_TYPES.includes(file.type) || file.size > MAX_LOGO_BYTES) {
            toast.error('Choose a PNG or JPG up to 5 MB');
            return;
        }
        set('logo', URL.createObjectURL(file));
    };

    const save = () => {
        setSaved(form);
        toast.info('Preview only: this isn’t connected yet, so the company details were not saved');
    };

    const actions = { dirty, onSave: save, onDiscard: () => setForm(saved) };
    const people = PEOPLE(name);

    return <>
        <SettingsHeader title="Company Details" sub={sub} {...actions} />
        <RecordBanner />

        <div className="cs-row">
            <div className="cs-main">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiHome /></span>
                        <div className="text">
                            <h2>Company Details</h2>
                            <div className="hint">Shown on the Website Hub and in emails</div>
                        </div>
                    </div>

                    <div className="logo-row">
                        {form.logo
                            ? <img className="logo" src={form.logo} alt={`${name} logo`} />
                            : <span className="logo mark">{initials(name)}</span>}
                        <div>
                            <div className="label">Company logo</div>
                            <div className="hint">PNG or JPG, at least 256 × 256 px</div>
                            <div className="actions">
                                <button type="button" className="btn-outline" onClick={() => fileRef.current?.click()}>{form.logo ? 'Upload New' : 'Upload Logo'}</button>
                                {form.logo && <button type="button" className="remove" onClick={() => set('logo', null)}>Remove</button>}
                                <input ref={fileRef} type="file" accept={LOGO_TYPES.join(',')} hidden onChange={pickLogo} />
                            </div>
                        </div>
                    </div>

                    <div className="fields three">
                        <div className="field">
                            <span className="label">Company name <em>· managed by Investi</em></span>
                            <div className="value-box locked"><span>{name}</span><ShieldCheckIcon /></div>
                        </div>
                        <div className="field">
                            <span className="label">Ticker <em>· managed by Investi</em></span>
                            <div className="value-box locked"><span>{ticker}</span><ShieldCheckIcon /></div>
                        </div>
                        <div className="field">
                            <span className="label">Exchange <em>· managed by Investi</em></span>
                            <div className="value-box locked"><span>{exchange}</span><ShieldCheckIcon /></div>
                        </div>
                    </div>

                    <div className="fields three">
                        <label className="field">
                            <span className="label">Website</span>
                            <input type="url" value={form.website} placeholder="https://www.company.com.au" onChange={e => set('website', e.target.value)} />
                        </label>
                        <div className="field">
                            <span className="label">Time zone</span>
                            <Dropdown value={form.timeZone} options={TIME_ZONE_OPTIONS} onChange={v => set('timeZone', v)} searchable />
                        </div>
                        <div className="field">
                            <span className="label">Financial year end</span>
                            <Dropdown value={form.yearEnd} options={YEAR_ENDS} onChange={v => set('yearEnd', v)} />
                        </div>
                    </div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiUser /></span><h2>Who Can Access</h2>
                        <button type="button" className="link in-title" onClick={() => history.push('/client-settings/users')}>Manage <FiArrowRight /></button>
                    </div>
                    {people.map(p => <div key={p.id} className="person">
                        <span className="av">{initials(p.name)}</span>
                        <div className="text">
                            <strong>{p.name}</strong>
                            <span>{p.organisation} · {p.role}</span>
                        </div>
                    </div>)}
                    <button type="button" className="link at-bottom" onClick={() => history.push('/client-settings/users')}>Manage <FiArrowRight /></button>
                </section>
            </div>
        </div>

        <div className="action-bottom"><SettingsActions {...actions} /></div>
    </>;
};

export default Company;
